
import Controller, { OnAfterNavigate, OnBeforeSubmit, OnChange, OnClick, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ActionAPI, AutocompleteAPI, CheckboxAPI, CheckboxProps, DateFieldAPI, FormAPI, InputAPI, LocatorAPI, RadioGroupAPI, SelectableItem, TextAreaAPI, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';

import { Inject } from '@odinjs/odin';

import moment from 'moment';

import BaseController from '../../../BaseController';
import DialogService from '../../../../services/dialog-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { DiseasesHighlightIntensitySeverityId, DiseasesRadioGroupId } from '../radio-group-data/diseases-radio-group-expressions';
import { DiseasesType, DomainType, HealthHistoryDiseasesRequest, HealthHistoryDiseasesResponse, HealthHistoryType, RadioType, SaeSearch, SearchType } from '../../health-history-types';
import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, HealthHistoryDiseasesEditAliases } from '../../../../codegen';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { ISO_FULL_DATE_FORMAT } from '../../../../../src/constants/date-utils';

enum WizardSteps {
  STEP_ONE = 'stepOne',
  STEP_TWO = 'stepTwo',
  STEP_THREE = 'stepThree'
}

@Controller({ component: HealthHistoryDiseasesEditAliases.LAYOUT_HEALTH_HISTORY_DISEASES_EDIT, fragment: FragmentNames.HEALTH_HISTORY_DISEASES_EDIT })
export default class HealthHistoryDiseasesEditController extends BaseController {

  @Inject(HealthHistoryDiseasesEditAliases.FORM_ONE)
  private formOneDiseasesEditAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesEditAliases.FORM_TWO)
  private formTwoDiseasesEditAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesEditAliases.FORM_THREE)
  private formThreeDiseasesEditAPI!: FormAPI;

  @Inject(HealthHistoryDiseasesEditAliases.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(HealthHistoryDiseasesEditAliases.CHECKBOX_DENIES_DISEASES)
  private checkboxDeniesdDiseasesAPI!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesEditAliases.CHECKBOX_INSERT_DIAGNOSIS_LIST)
  private checkboxInsertDiagnosisApi!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesEditAliases.CHECKBOX_DISPLAY_PATIENT_ALERTS)
  private checkboxAlertsAPI!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesEditAliases.INPUT_CID)
  private cidInputAPI!: InputAPI;

  @Inject(HealthHistoryDiseasesEditAliases.LOCATOR_CID)
  private locatorCidAPI!: LocatorAPI

  @Inject(HealthHistoryDiseasesEditAliases.INPUT_DISEASESFREE)
  private inputDiseaseFreeAPI!: InputAPI

  @Inject(HealthHistoryDiseasesEditAliases.INPUT_SAE)
  private saeInputAPI!: InputAPI;

  @Inject(HealthHistoryDiseasesEditAliases.LOCATOR_SAE)
  private locatorSaeAPI!: LocatorAPI

  @Inject(HealthHistoryDiseasesEditAliases.ACTION_DELETE_HEALTH_HISTORY_DISEASES)
  private actionDeleteAPI!: ActionAPI;

  @Inject(HealthHistoryDiseasesEditAliases.ACTION_NEW_HEALTH_HISTORY_DISEASES)
  private actionNewAPI!: ActionAPI;

  @Inject(HealthHistoryDiseasesEditAliases.ACTION_RELEASE_HEALTH_HISTORY_DISEASES)
  private actionReleaseAPI!: ActionAPI;

  @Inject(HealthHistoryDiseasesEditAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY)
  private radioGroupHighlightIntensitySeverityAPI!: RadioGroupAPI;

  @Inject(HealthHistoryDiseasesEditAliases.AUTOCOMPLETE_SITUATION)
  private autocompleteSituationApi!: AutocompleteAPI;

  @Inject(HealthHistoryDiseasesEditAliases.AUTOCOMPLETE_SECURITYLEVEL)
  private autocompleteSecurityLevelApi!: AutocompleteAPI;

  @Inject(HealthHistoryDiseasesEditAliases.DATE_FIELD_START_DATE)
  private dateFieldStartDateApi!: DateFieldAPI;

  @Inject(HealthHistoryDiseasesEditAliases.DATE_FIELD_END_DATE)
  private dateFieldEndDateApi!: DateFieldAPI;

  @Inject(HealthHistoryDiseasesEditAliases.AUTOCOMPLETE_PATHOLOGIE)
  private autocompletePathologieApi!: AutocompleteAPI;

  @Inject(HealthHistoryDiseasesEditAliases.CHECKBOX_INSERT_PROBLEMS_LIST)
  private checkboxInsertProblemsApi!: CheckboxAPI;

  @Inject(HealthHistoryDiseasesEditAliases.TEXT_AREA_APPROACH)
  private textAreaApproachApi!: TextAreaAPI;

  @OnAfterNavigate(HealthHistoryDiseasesEditAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  @OnValidate(HealthHistoryDiseasesEditAliases.FORM_ONE)
  handleOnValidate(validation: ValidateEvent): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_ONE, validation.valid);
  }

  @OnValidate(HealthHistoryDiseasesEditAliases.FORM_TWO)
  handleOnSecondValidate(validationFormTwo: ValidateEvent): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, validationFormTwo.valid);
  }

  @OnValidate(HealthHistoryDiseasesEditAliases.FORM_THREE)
  handleOnTirdValidate(validationFormThree: ValidateEvent): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, validationFormThree.valid);
  }

  @OnMount({ target: HealthHistoryDiseasesEditAliases.FORM_ONE })
  handleMountForm(): void {
    applicationContext.popNavigationItem();
    this.loadEditionData()
    this.formOneDiseasesEditAPI.setLoading(true);
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
  }

  @OnMount({ target: HealthHistoryDiseasesEditAliases.WIZARD })
  handleFormMount(): void {
    HealthHistoryListContext.currentTab = HealthHistoryType.DISEASES
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-diseases',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  @OnChange({ target: [HealthHistoryDiseasesEditAliases.FIELD_CID] })
  handleClassificationChange(event: ChangeEvent<DiseasesType>): void {
    const value = event.value;
    const oldValue = event.oldValue;

    if (oldValue === value) {
      return;
    }

    if (value?.diseaseCode) {
      this.disableSaeFields();

      this.inputDiseaseFreeAPI.setEnabled(false)
      this.checkboxDeniesdDiseasesAPI.setEnabled(false);
      this.checkboxInsertDiagnosisApi.setEnabled(true);
    } else {
      this.enableSaeFields();

      this.inputDiseaseFreeAPI.setEnabled(true);
      this.checkboxInsertDiagnosisApi.setEnabled(false);
      this.checkboxInsertDiagnosisApi.setValue(false);
      this.checkboxDeniesdDiseasesAPI.setEnabled(true);
    }
  }

  @OnChange({ target: [HealthHistoryDiseasesEditAliases.FIELD_SAE] })
  handleSaeChange(event: ChangeEvent<SaeSearch>): void {
    const value = event.value;
    const oldValue = event.oldValue;

    if (oldValue === value) {
      return;
    }

    if (value?.id) {
      this.disableCidFields();

      this.inputDiseaseFreeAPI.setEnabled(false);
      this.checkboxDeniesdDiseasesAPI.setEnabled(false);
    } else {
      this.enableCidFields();
      this.inputDiseaseFreeAPI.setEnabled(true);
      this.checkboxDeniesdDiseasesAPI.setEnabled(true);
    }
  }

  @OnMount({ target: HealthHistoryDiseasesEditAliases.INPUT_DISEASESFREE })
  handleMountDiseaseFree(event: MountEvent): void {
    const value = event.target as InputAPI;
    this.enableOrDisableFieldsAfterInputDiseasesFree(value.getValue());
  }

  @OnChange({ target: HealthHistoryDiseasesEditAliases.INPUT_DISEASESFREE })
  handleDiseaseFree(event: ChangeEvent<InputAPI>): void {
    const value = event.value;
    const oldValue = event.oldValue;

    if (oldValue === value) {
      return;
    }

    this.enableOrDisableFieldsAfterInputDiseasesFree(value);
  }

  private enableOrDisableFieldsAfterInputDiseasesFree(value: Value): void {
    if (value) {
      this.disableCidFields();
      this.disableSaeFields();

      this.checkboxDeniesdDiseasesAPI.setEnabled(false);
    } else {
      this.enableCidFields();
      this.enableSaeFields();

      this.checkboxDeniesdDiseasesAPI.setEnabled(true);
    }
  }

  @OnChange({ target: HealthHistoryDiseasesEditAliases.CHECKBOX_DENIES_DISEASES })
  handleDeniesDiseasesChange({ value }: ChangeEvent<CheckboxProps>): void {
    this.enableOrDisableFieldsAfterDeniesDisease(value);
  }

  private disableCidFields(): void {
    this.locatorCidAPI.setEnabled(false);
    this.cidInputAPI.setEnabled(false);
    this.locatorCidAPI.setInputEnabled(false);
  }

  private enableCidFields(): void {
    this.locatorCidAPI.setEnabled(true);
    this.cidInputAPI.setEnabled(true);
    this.locatorCidAPI.setInputEnabled(true);
  }

  private disableSaeFields(): void {
    this.locatorSaeAPI.setEnabled(false);
    this.saeInputAPI.setEnabled(false);
    this.locatorSaeAPI.setInputEnabled(false);
  }

  private enableSaeFields(): void {
    this.locatorSaeAPI.setEnabled(true);
    this.saeInputAPI.setEnabled(true);
    this.locatorSaeAPI.setInputEnabled(true);
  }

  private enableOrDisableFieldsAfterDeniesDisease(value: CheckboxProps | Value): void {
    if (value) {
      this.disableCidFields();
      this.disableSaeFields();

      this.autocompleteSituationApi.setValue({});
      this.autocompleteSituationApi.setEnabled(false);
      this.inputDiseaseFreeAPI.setEnabled(false);
      this.autocompleteSecurityLevelApi.setValue({});
      this.autocompleteSecurityLevelApi.setEnabled(false);
      this.dateFieldStartDateApi.setValue('');
      this.dateFieldStartDateApi.setEnabled(false);
      this.dateFieldEndDateApi.setValue('');
      this.dateFieldEndDateApi.setEnabled(false);
      this.autocompletePathologieApi.setValue({});
      this.autocompletePathologieApi.setEnabled(false);

      this.radioGroupHighlightIntensitySeverityAPI.setEnabled(false);
      this.checkboxInsertProblemsApi.setValue(false);
      this.checkboxInsertProblemsApi.setEnabled(false);
      this.checkboxInsertDiagnosisApi.setValue(false);
      this.checkboxInsertDiagnosisApi.setEnabled(false);
      this.textAreaApproachApi.setValue('');
      this.textAreaApproachApi.setEnabled(false);
    } else {
      this.enableCidFields();
      this.enableSaeFields();

      this.autocompleteSituationApi.setEnabled(true);
      this.inputDiseaseFreeAPI.setEnabled(true);
      this.autocompleteSecurityLevelApi.setEnabled(true);
      this.dateFieldStartDateApi.setEnabled(true);
      this.dateFieldEndDateApi.setEnabled(true);
      this.autocompletePathologieApi.setEnabled(true);
      this.radioGroupHighlightIntensitySeverityAPI.setEnabled(true);
      this.checkboxInsertProblemsApi.setEnabled(true);
      this.textAreaApproachApi.setEnabled(true);
    }
  }

  private validateEnabledFieldsAfterLoadData(healthHistoryDisease: HealthHistoryDiseasesResponse): void {
    const deniesDiseases = BooleanUtils.isTrueValue(healthHistoryDisease.deniesDiseases);
    const diseasesFree = healthHistoryDisease.diseaseAvailableDescription;

    if (deniesDiseases) {
      this.enableOrDisableFieldsAfterDeniesDisease(true);
    }
    if (diseasesFree) {
      this.enableOrDisableFieldsAfterInputDiseasesFree(diseasesFree);
    }
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.historyService.back(); },
      onClickOk: () => { this.releaseDisease(idHealthHistory) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
  }

  private releaseDisease(idHealthHistory: number): void {
    this.formOneDiseasesEditAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_DISEASES, { sequenceNumberDisease: idHealthHistory })
      .then(() => {
        this.formOneDiseasesEditAPI.setLoading(false);
        this.goBack();
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneDiseasesEditAPI.setLoading(false)
      });
  }

  private createDiseasesHistory(body: Record<string, Value>): void {
    this.formOneDiseasesEditAPI.setLoading(true);
    HttpService.post(Endpoint.CREATE_HEALTH_HISTORY_DISEASES, undefined, { body })
      .then(response => {
        const { idHealthHistory } = response.body as HealthHistoryDiseasesResponse;
        this.showModalSubmit(idHealthHistory);
      })
      .catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formOneDiseasesEditAPI.setLoading(false));
  }

  private loadEditionData(): Promise<void> {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    return HttpService.get(Endpoint.HEALTH_HISTORY_DISEASES, { sequenceNumberDisease: idHealthHistory })
      .then(res => {
        const record = res.body as HealthHistoryDiseasesResponse;
        this.treatData({ ...record });
        this.wizardAPI.setStepDone(WizardSteps.STEP_ONE, true);
        this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
        this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
      }).catch((err) => this.showToast(err?.body, ToastLevel.ERROR))
      .finally(() => {
        this.formOneDiseasesEditAPI.setLoading(false)
        this.actionDeleteAPI.setEnabled(true);
        this.actionNewAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);
      });
  }

  @OnBeforeSubmit({ target: HealthHistoryDiseasesEditAliases.WIZARD })
  handleBeforeSubmit(): void {
    const { patientId, idHealthHistory, encounterId } = this.historyService.getCurrentRouteParams();
    const formDataOne = this.formOneDiseasesEditAPI.getRecord();
    const formDataTwo = this.formTwoDiseasesEditAPI.getRecord();
    const formDataThree = this.formThreeDiseasesEditAPI.getRecord();

    const displayPatientAlerts = BooleanUtils.isPositiveValue(this.checkboxAlertsAPI.getValue() as boolean);
    const approach = formDataThree.approachDescription;
    const deniesDesiases = BooleanUtils.isPositiveValue(formDataOne.deniesDiseases as boolean);
    const diseaseAvailableDescription = formDataOne.diseaseAvailableDescription;
    const disease = formDataOne.disease as DiseasesType;
    const sae = formDataOne.sae as SaeSearch;
    const status = formDataOne.status as DomainType;
    const securityLevel = formDataOne.securityLevel as SearchType;
    const pathology = formDataTwo.pathology as DomainType;
    const endDate = formDataTwo.endDate ? moment(String(formDataTwo.endDate)).utc().format(ISO_FULL_DATE_FORMAT) : '';
    const intensity = this.radioGroupHighlightIntensitySeverityAPI.getValue() as RadioType;
    const insertDiagnosis = BooleanUtils.isPositiveValue(formDataThree.insertDiagnosis as boolean);
    const notes = formDataThree.notes || '';
    const problemList = BooleanUtils.isPositiveValue(formDataThree.insertProblemsList as boolean);
    const recordDate = formDataOne.recordDate ? moment(String(formDataOne.recordDate)).utc().format(ISO_FULL_DATE_FORMAT) : '';
    const startDate = formDataTwo.startDate ? moment(String(formDataTwo.startDate)).utc().format(ISO_FULL_DATE_FORMAT) : '';
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;

    const body = {
      approach,
      attendance: encounterId as string,
      deniesDesiases,
      diseaseAvailableDescription,
      diseaseCode: disease.diseaseCode,
      displayPatientAlerts,
      endDate,
      idHealthHistory: idHealthHistory as string,
      insertDiagnosis,
      intensity: intensity ? intensity.value : '',
      notes: notes ? notes : '',
      pathologyCode: pathology ? pathology.valueDomain : '',
      physicalPerson: patientId,
      problemList,
      professional: user.physicalPersonId,
      recordDate,
      saeCode: sae ? sae.id : '',
      securityLevelCode: securityLevel ? securityLevel.id : '',
      startDate,
      status: status ? status.valueDomain : ''
    } as Record<string, Value>;
    encounterId && this.createDiseasesHistory(body);
  }

  private treatData(record: HealthHistoryDiseasesResponse): void {

    const displayPatientAlerts = BooleanUtils.isTrueValue(record.displayPatientAlerts) && true;
    const insertDiagnosis = BooleanUtils.isTrueValue(record.insertDiagnosis) && true;
    const approach = record.approachDescription || '';
    const attendance = record.attendance || '';
    const pathology = record.pathology as DomainType;
    const status = record.status as DomainType;
    const deniesDiseases = BooleanUtils.isTrueValue(record.deniesDiseases) && true;
    const disease = record.disease;
    const sae = record.sae;
    const securityLevel = record.securityLevel
    const diseaseAvailableDescription = record.diseaseAvailableDescription;
    const notes = record.notes || '';
    const endDate = record.endDate ? moment(record.endDate) : '';
    const highlightIntensitySeverity = record.highlightIntensitySeverity || '';
    const insertProblemsList = BooleanUtils.isTrueValue(record.insertProblemsList) && true;
    const recordDate = moment(record.recordDate);
    const startDate = moment(record.startDate);

    const data = {
      ...record,
      approach,
      attendance,
      deniesDiseases,
      disease: disease ? disease : '',
      diseaseAvailableDescription,
      displayPatientAlerts,
      endDate,
      insertDiagnosis,
      insertProblemsList,
      intensity: highlightIntensitySeverity,
      notes,
      pathology,
      recordDate,
      sae: sae ? sae : '',
      securityLevel,
      startDate,
      status
    }
    this.context.set({ healthHistoryDiseases: record });
    this.formOneDiseasesEditAPI.setData(data);
    this.formTwoDiseasesEditAPI.setData(data);
    this.formThreeDiseasesEditAPI.setData(data);
    this.setRadioGroupValues(DiseasesRadioGroupId.HIGHLIGHT_INTENSITY_SEVERITY, this.radioGroupHighlightIntensitySeverityAPI);
    this.validateEnabledFieldsAfterLoadData(record);
  }

  private setRadioGroupValues(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        if (response) {
          const responseBody = response.body as SelectableItem[];
          radioGroup.setData(responseBody);

          const { highlightIntensitySeverity } = this.context.get('healthHistoryDiseases') as HealthHistoryDiseasesRequest;

          switch (highlightIntensitySeverity) {
            case DiseasesHighlightIntensitySeverityId.MILD_LITTLE:
              radioGroup.setValue(responseBody[0])
              break;
            case DiseasesHighlightIntensitySeverityId.MEDIUM_MODERATE:
              radioGroup.setValue(responseBody[1])
              break;
            case DiseasesHighlightIntensitySeverityId.SEVERE_LARGE:
              radioGroup.setValue(responseBody[2])
              break;
            default:
              break;
          }
        }
      }).catch((error) => {
        this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private delete(idHealthHistory: number): void {
    const idHealthHistoryDiseases = idHealthHistory;
    this.formOneDiseasesEditAPI.setLoading(true);
    HttpService.delete(Endpoint.DELETE_HEALTH_HISTORY_DISEASES, { idHealthHistoryDiseases })
      .then(() => {
        this.formOneDiseasesEditAPI.setLoading(false);
        this.goBack();
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneDiseasesEditAPI.setLoading(false)
      });
  }

  private confirmDelete(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(idHealthHistory); },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  @OnClick({ target: HealthHistoryDiseasesEditAliases.ACTION_NEW_HEALTH_HISTORY_DISEASES })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_DISEASES_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: HealthHistoryDiseasesEditAliases.ACTION_DELETE_HEALTH_HISTORY_DISEASES })
  handleClickDelete(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idHealthHistory as number);
  }

  @OnClick({ target: HealthHistoryDiseasesEditAliases.ACTION_RELEASE_HEALTH_HISTORY_DISEASES })
  handleClickRelease(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idHealthHistory as number);
  }

  @OnUnmount(HealthHistoryDiseasesEditAliases.LAYOUT_HEALTH_HISTORY_DISEASES_EDIT)
  handleUnmount(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.DISEASES;
  }
}
